<template>
  <div>
    <ContentDetailGallery :variant="variant" class="float-none md:float-left mr-0 md:mr-5" />
    <div class="flex flex-row items-center justify-between gap-2 mb-4 md:mb-8 pt-5 pl-5 md:pl-0 pr-5 md:pr-10">
      <h1 class="font-bold text-2xl tracking-tight text-primary-dark cursor-pointer transition-colors hover:text-primary-dark-hover no-underline hover:underline" @click="navigate">
          {{ localized(variant, 'name', variant.name, !showOriginal) }}
      </h1>
      <ContentFavorite v-if="!isAnonymized" :variant="variant" class="flex-shrink-0" />
    </div>

    <div :class="['mx-5 md:mx-10', translating ? 'animate-pulse' : '', !translating && !_.find(variant.languages || [], { isTranslated: true }) ? 'mb-9' : 'mb-5']">
      <article
        class="prose max-w-none break-words"
        v-html="contentHtml"
      />
    </div>
    <Alert v-if="translating" class="mb-5 mx-5 md:mx-10" variant="light">
      <div class="flex flex-row space-x-2 items-center">
        <LanguageIcon class="w-4 h-4" />
        <div>{{ $t('text-translation-in-progress', { name: _.get(_.find(enums.languages, { locale: routeLang.locale.value }), 'name', '') }) }}</div>
      </div>
    </Alert>
    <Alert v-else-if="_.find(variant.languages || [], { isTranslated: true })" class="mb-5" variant="light">
      <div class="flex flex-row space-x-2 items-center">
        <LanguageIcon class="w-4 h-4" />
        <div v-if="!showOriginal">{{ $t('text-translated') }}</div>
        <button
          class="underline text-accent font-semibold transition-colors duration-200 focus:outline-none hover:text-accent-hover focus:text-accent-hover hover:no-underline"
          @click="() => showOriginal = !showOriginal"
        >
          {{ showOriginal ? $t('text-show-translation') : $t('text-show-original') }}
        </button>
      </div>
    </Alert>

    <div v-if="!isAnonymized && _.get(variant, 'supplier.id') === auth?.idSupplier" class="mb-9 mx-5 md:mx-10">
      <div class="flex flex-col">
        <div class="mb-3 lg:mb-0">
          <EditContentBtn
            :data="variant"
            variant="big"
            class="!w-full"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-row items-center justify-between mb-9 mx-5 md:mx-10">
      <div class="flex flex-row items-center justify-start space-x-2.5">
        <ContentViews :variant="variant" />
        <ContentLikes :variant="variant" />
        <MessagesBadge :variant="variant" :on-click="() => $emit('select:tab:comments', props.variant.id)" />
      </div>
      <ReportContent :variant="variant" />
    </div>

    <div v-if="!isAnonymized" class="flex items-center justify-between space-x-2 mb-9 mx-5 md:mx-10">
      <ProfileHeader v-if="!isAnonymized" :data="variant" />
      <ContentShare :variant="variant" />
    </div>

    <div class="px-5 md:px-10 mb-9">
      <ContentMessages v-if="!isAnonymized" :content="variant" />
    </div>

  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { ROUTES } from "@utils/routes";
import { STATUS } from "@utils/constants";
import { useEnumStore } from '@stores/enum';
import { useAuthStore } from '@stores/auth';
import ProfileHeader from "@components/profile/profile-header";
import ContentFavorite from "@components/content/content-details/content-favorite";
import EditContentBtn from "@components/content/content-edit/edit-content-btn";
import ContentViews from "@components/content/content-details/content-views";
import ContentLikes from "@components/content/content-details/content-likes";
import MessagesBadge from "@components/common/messages-badge";
import ReportContent from "@components/content/content-details/report-content";
import ContentShare from "@components/content/content-details/content-share";
import ContentMessages from "@components/content/content-details/content-messages";
import ContentDetailGallery from "@components/content/content-details/content-detail-gallery";
import Alert from "@components/ui/alert";
import LanguageIcon from "@components/icons/language-icon";

defineEmits(['select:tab:comments']);

const props = defineProps({
  variant: {
    type: Object,
    required: true
  },
  size: {
    type: String,
    default: 'page' //page, modal
  }
})

const { $eventBus } = useNuxtApp();
const enums = useEnumStore();
const auth = useAuthStore();
const routeLang = useRouteLang();
const activityChecker = useActivityChecker();
const translating = ref(false);
const showOriginal = ref(false);

const contentHtml = computed(() => {
  const translated = !showOriginal.value;
  const html = localized(props.variant, 'content_html', '', translated);

  return html || sanitizeLinks(markdownit(localized(props.variant, 'content', '', translated)));
});

const routeCode = computed(() => {
  return {
    inspiration: ROUTES.CODE.INSPIRATION,
    news: ROUTES.CODE.NEWS
  }[props.variant?.type] || ROUTES.CODE.PRODUCT;
});

const isCzechSlovakPair = (sourceLocale, targetLocale) => {
  return (sourceLocale === 'cs_CZ' && targetLocale === 'sk_SK') || (sourceLocale === 'sk_SK' && targetLocale === 'cs_CZ');
};

const shouldSkipAutoTranslation = (type) => {
  const currentLocale = routeLang.locale.value;

  if (!currentLocale || isLocalized(props.variant, type) || !localized(props.variant, type)) {
    return false;
  }

  const originalLanguage = _.find(props.variant.languages || [], { type, isTranslated: false });

  return isCzechSlovakPair(_.get(originalLanguage, 'language.locale'), currentLocale);
};

const translateVariant = async () => {
  if (!activityChecker.wasUserActive.value) {
    return;
  }

  const typesNeedingTranslation = ['name', 'content'].filter(type => !isLocalized(props.variant, type) && localized(props.variant, type));

  if (typesNeedingTranslation.length && typesNeedingTranslation.every(shouldSkipAutoTranslation)) {
    return;
  }

  if ((!isLocalized(props.variant, 'name') && localized(props.variant, 'name')) || (!isLocalized(props.variant, 'content') && localized(props.variant, 'content'))) {
    translating.value = true;
    const result = await contentPostTranslationLoad(props.variant.id);
    _.assignIn(props.variant, result);
    translating.value = false;
  } else if (!_.find(props.variant.languages || [], { isTranslated: false })) {
    const result = await contentPostTranslationLoad(props.variant.id);
    _.assignIn(props.variant, result);
  }
}

watch(() => routeLang.locale.value, async () => {
  if (!translating.value) {
    await nextTick(translateVariant);
  }
});

watch(() => activityChecker.wasUserActive.value, async (value) => {
  if (value && !translating.value) {
    await nextTick(translateVariant);
  }
});

const isAnonymized = computed(() => _.includes([STATUS.HIDDEN, STATUS.CLOSED], props.variant?.status));

const closeModal = () => {
    $eventBus.emit('modal:close');
}

const navigate = async () => {
    closeModal();
    await navigatorTo(translatePath(routeCode.value, slugify(props.variant?.id, props.variant?.name)));
}

onMounted(async () => {
  if (import.meta.client) {
    _.delay(translateVariant, 1000);
  }
});

</script>
